import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { TextField, Card } from '@mui/material';
import Button from 'react-bootstrap/Button';
import { memberHandler } from '../../redux/handlers';
import { createTableColumns } from '../../utils/createTableColumns';
import { useGlobalTable } from '../../hooks/useGlobalTable';
import AllderTable from '../../components/AllderTable';
import { ConfirmDeleteModal, CustomerGroupFormModal, SuccessModal } from './modals';

const TEXTFIELD_STYLES = {
  InputProps: { sx: { borderRadius: '7px' } },
  sx: {
    '.Mui-disabled': {
      background: '#F8F8F9'
    }
  },
  className: 'w-[200px] !ml',
  size: 'small'
};

export function CustomerGroup() {
  const { businessSlug } = useParams();
  const dispatch = useDispatch();
  const [editData, setEditdata] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchMemberGroupData = useCallback(
    async params => {
      const queryParams = {
        businessSlug: businessSlug,
        perPage: params.per_page,
        ...params
      };
      return dispatch(memberHandler.getMemberGroupPagination(businessSlug, queryParams));
    },
    [dispatch, businessSlug]
  );

  const {
    data: groupData,
    loading,
    totalRows,
    paginationModel,
    setPaginationModel,
    handleSort,
    debouncedSearch,
    refreshData,
    filterParams
  } = useGlobalTable({
    fetchData: fetchMemberGroupData,
    defaultSort: 'created_at',
    defaultOrder: 'desc',
    defaultPageSize: 10
  });

  const handleExport = () => {
    const params = {
      s: filterParams.s
    };
    dispatch(memberHandler.getMemberGroupExcel(businessSlug, params));
  };

  const handleDelete = row => {
    setEditdata(row);
    setModalConfirmDelete(prev => ({ ...prev, open: true }));
  };

  const columns = createTableColumns([
    {
      field: 'name',
      headerName: 'ชื่อกลุ่มลูกค้า',
      width: 170,
      sortable: true,
      flex: 1,
      renderCell: ({ row }) => (
        <Link
          to={`/${businessSlug}/customer-group/${row.id}`}
          style={{ color: '#2DC76D', fontWeight: 500, textDecoration: 'none' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          {row.name || ''}
        </Link>
      )
    },
    {
      field: 'customers',
      headerName: 'จำนวนสมาชิก',
      flex: 1,
      width: 140,
      sortable: false,
      renderCell: ({ row }) => <div>{row.member?.length || 0}</div>
    },
    {
      field: 'status',
      headerName: 'สถานะ',
      width: 180,
      sortable: false,
      renderCell: ({ row }) => <div className={`datalist-status ${row.is_active ? 'status1' : 'status0'}`} />
    },
    {
      field: 'actions',
      headerName: 'จัดการ',
      width: 250,
      sortable: false,
      renderCell: ({ row }) => (
        <div className="d-flex">
          <Button
            type="button"
            className="btn-edit-st-1"
            onClick={() => {
              setEditdata(row);
              setModalForm(prev => ({ ...prev, open: true }));
            }}
            title="แก้ไข"
          />
          <Button type="button" className="btn-delete-st-3 ml-2" onClick={() => handleDelete(row)} title="ลบ" />
        </div>
      )
    }
  ]);

  const [modalForm, setModalForm] = useState({
    open: false,
    x: 0,
    y: 0,
    width: 470,
    height: 300,
    pin: false,
    mode: 'normal',
    modalname: 'form'
  });

  const [modalConfirmDelete, setModalConfirmDelete] = useState({
    open: false,
    x: 0,
    y: 0,
    width: 470,
    height: 320,
    pin: false,
    mode: 'normal',
    modalname: 'confirmDelete'
  });

  const [modalSuccess, setModalSuccess] = useState({
    open: false,
    x: 0,
    y: 0,
    width: 470,
    height: 300,
    pin: false,
    mode: 'normal',
    modalname: 'success'
  });

  const handleConfirmDelete = async () => {
    try {
      await dispatch(memberHandler.deleteMemberGroup(editData.id));
      setModalConfirmDelete(prev => ({ ...prev, open: false }));
      setSuccessMessage('ลบกลุ่มลูกค้าสำเร็จ');
      setModalSuccess(prev => ({ ...prev, open: true }));
      refreshData();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleFormSuccess = (message = 'บันทึกข้อมูลสำเร็จ') => {
    setModalForm(prev => ({ ...prev, open: false }));
    setSuccessMessage(message);
    setModalSuccess(prev => ({ ...prev, open: true }));
    setEditdata(null);
    refreshData();
  };

  const handleCreateClick = () => {
    setEditdata(null);
    setModalForm(prev => ({ ...prev, open: true }));
  };

  const handleCloseForm = () => {
    setModalForm(prev => ({ ...prev, open: false }));
    setEditdata(null);
  };

  const handleCloseSuccess = () => {
    setModalSuccess(prev => ({ ...prev, open: false }));
    setSuccessMessage('');
  };

  return (
    <div>
      <nav aria-label="breadcrumb" className="mt-2">
        <ol className="breadcrumb fs-16 ps-0">
          <li className="breadcrumb-item">
            <a href="#" className="tc-7 text-decoration-none">
              สมาชิก/ลูกค้า
            </a>
          </li>
          <li className="breadcrumb-item active tc-1" aria-current="page">
            จัดการกลุ่มลูกค้า
          </li>
        </ol>
      </nav>

      <Card className="!rounded-lg mb-4">
        <div className="py-4">
          <div className="fs-18 fw-bold me-auto px-4 mb-4" style={{ color: '#2DC76D' }}>
            จัดการกลุ่มลูกค้า
          </div>

          <div className="d-flex justify-content-between align-items-center px-4 mb-3 flex-wrap">
            <div className="d-flex align-items-center mb-2">
              <button className="btn btn-export-st-1" onClick={handleExport}>
                Export
              </button>
            </div>

            <div className="d-flex align-items-center mb-2">
              <div className="me-3">
                <TextField
                  {...TEXTFIELD_STYLES}
                  className="w-[250px]"
                  placeholder="ค้นหาชื่อกลุ่มลูกค้า"
                  onChange={e => debouncedSearch(e.target.value)}
                />
              </div>

              <Button type="button" className="btn btn-st-7" onClick={handleCreateClick}>
                เพิ่มกลุ่มลูกค้า
              </Button>
            </div>
          </div>

          <AllderTable
            data={groupData}
            loading={loading}
            columns={columns}
            totalRows={totalRows}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            handleSort={handleSort}
          />
        </div>
      </Card>

      <CustomerGroupFormModal
        modal={modalForm}
        setModal={setModalForm}
        editData={editData}
        onSuccess={handleFormSuccess}
        onClose={handleCloseForm}
        onDelete={() => {
          setModalConfirmDelete(prev => ({ ...prev, open: true }));
          setModalForm(prev => ({ ...prev, open: false }));
        }}
      />

      <ConfirmDeleteModal
        modal={modalConfirmDelete}
        setModal={setModalConfirmDelete}
        onHide={() => setModalConfirmDelete(prev => ({ ...prev, open: false }))}
        onConfirm={handleConfirmDelete}
        editData={editData}
      />

      <SuccessModal
        modal={modalSuccess}
        setModal={setModalSuccess}
        onHide={handleCloseSuccess}
        textsuccess={successMessage}
      />
    </div>
  );
}
